import { apiErrorMessage, isServerError } from '@store/error';

/**
 * What asking for a preview comes to: the address of the PDF, which the preview gives back
 * with `URL.revokeObjectURL` once it has drawn a newer one, or why there is none. `error` is
 * the server's own words, which may be none; `status` is what the preview words its message by.
 */
export type PreviewAnswer = { url: string } | { error: string; status?: number };

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null;

/**
 * The result of a preview mutation as the answer `PdfPreview` takes. The mutation resolves
 * with the PDF's object URL, or is refused with the API's usual body; a renderer that threw
 * (422) carries its message under `errors.message`.
 */
export const toPreviewAnswer = (result: { data?: unknown; error?: unknown }): PreviewAnswer => {
  if (!result.error) return typeof result.data === 'string' ? { url: result.data } : { error: '' };

  const failure = { error: result.error };

  if (isServerError(failure)) {
    const { status, data } = failure.error;
    const detail = isRecord(data.errors) && typeof data.errors.message === 'string' ? data.errors.message : null;

    return { error: detail ?? apiErrorMessage(data), status };
  }

  // No answer at all, or one that is not the API's: a proxy's page, a dropped connection.
  const status = isRecord(result.error) ? result.error.originalStatus : undefined;

  // A 429 that is not the API's own is the request limiter's, which answers in plain text for
  // the whole installation. The preview words a 429 as "another preview of yours is being
  // drawn", which is true only of the API's; this one is passed on as a failure with no name.
  return { error: '', status: typeof status === 'number' && status !== 429 ? status : undefined };
};
